import { useState, useEffect, useRef } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import CustomRadioButton from "../../components/buttons/CustomRadioButton";
import AddModel from "../../components/Add/AddModel";
import AddBottomButton from "../../components/Add/AddBottomButton";
import PurchaseTypeDropdown from "./components/PurchaseTypeDropdown";
import { useDispatch } from "react-redux";
import { resetManualState, setCreditStatus, setMode } from "../../store/slices/addManualSlice";
import { useSelector } from "react-redux";
import useApi from "../../services/useApi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showToast } from "../../components/toastMessage/toastSlice";
import CheckBoxWithLabel from "../../components/checkbox/CheckBoxWithLabel";
import { inputFieldContent } from "../../components/inputfields/InputFieldContent";
import useTranslation from "../../components/CustomHooks/useTranslation";

export default function AddManual() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { notEnglish } = useTranslation(inputFieldContent)
    const { id } = useParams();
    const { PostAPI } = useApi();
    const [selectedOption, setSelectedOption] = useState<"Paid" | "Advance">("Paid");
    const [loading, setLoading] = useState(false);
    const sourceRefs = useRef<(HTMLDivElement | null)[]>([]);
    const allDetails = useSelector((state: any) => state.addManual || {});
    const [creditStatusLocal, setCreditStatusLocal] = useState(false);
    const [errors, setErrors] = useState<{
        purchase_type?: string;
        purchase_details?: {
            source_accounts?: Array<{
                source_account_id?: string;
                source_account_name?: string;
                purchased_amount?: string;
                purchased_date?: string;
                receipt_url?: string;
                note?: string;
            }>;
            destination_accounts?: Array<{
                destination_account_id?: string;
                destination_account_name?: string;
                purchased_amount?: string;
                purchased_date?: string;
                receipt_url?: string;
                note?: string;
            }>;
            credit_status?: string;
        };
    }>({
        purchase_type: "",
        purchase_details: {
            source_accounts: [
                {
                    source_account_id: "",
                    source_account_name: "",
                    purchased_amount: "",
                    purchased_date: "",
                    receipt_url: "",
                    note: "",
                },
            ],
            destination_accounts: [
                {
                    destination_account_id: "",
                    destination_account_name: "",
                    purchased_amount: "",
                    purchased_date: "",
                    receipt_url: "",
                    note: "",
                },
            ],
            credit_status: "",
        },
    });

    useEffect(() => {
        return () => {
            dispatch(resetManualState());
        };
    }, [location.pathname, dispatch]);

    const handleSubmit = async () => {
        const newErrors = {
            purchase_type: "",
            purchase_details: {
                source_accounts: allDetails.sources?.map((source: any) => {
                    if (!source.source_account_name) {
                        return { source_account_name: "This field is required" };
                    }
                    return {
                        source_account_name: "",
                        purchased_amount: !source.purchased_amount ? "This field is required" : "",
                        purchased_date: !source.purchased_date ? "This field is required" : "",
                        receipt_url: !source.receipt_url ? "This field is required" : "",
                    };
                }) || [],
                ...(selectedOption === 'Paid' && {
                    destination_accounts: allDetails.destinations?.map((destination: any) => {
                        if (!destination.destination_account_name) {
                            return { destination_account_name: "This field is required" };
                        }
                        return {
                            destination_account_name: "",
                            purchased_amount: !destination.purchased_amount ? "This field is required" : "",
                            purchased_date: !destination.purchased_date ? "This field is required" : "",
                            receipt_url: !destination.receipt_url ? "This field is required" : "",
                        };
                    }) || []
                })
            }
        };
        setErrors(newErrors);
        // Check if there are any errors
        const hasSourceErrors = newErrors.purchase_details.source_accounts.some(
            (error: any) => Object.values(error).some(value => value !== "")
        );
        let hasDestinationErrors = false;

        if (selectedOption === 'Paid') {
            hasDestinationErrors = newErrors.purchase_details.destination_accounts.some(
                (error: any) => Object.values(error).some(value => value !== "")
            );
        }

        if (hasSourceErrors || hasDestinationErrors) {
            // Find the first error index
            const errorIndex = newErrors.purchase_details.source_accounts.findIndex(
                (error: any) => Object.values(error).some(value => value !== "")
            );

            // Scroll to the error element using ref
            if (errorIndex !== -1 && sourceRefs.current[errorIndex]) {
                sourceRefs.current[errorIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
        }

        // Construct payload if no errors
        setLoading(true);
        const payload = {
            purchase_type: selectedOption,
            purchase_details: {
                source_accounts: allDetails.sources.map((source: { source_account_id: number | string, source_account_name: string, purchased_amount: string | number, purchased_date: string, receipt_url?: string, notes?: string }) => ({
                    source_account_id: source.source_account_id.toString(),
                    source_account_name: source.source_account_name,
                    purchased_amount: Number(source.purchased_amount),
                    purchased_date: source.purchased_date,
                    receipt_url: source.receipt_url,
                    ...(source.notes && { note: source.notes }),
                })),
                ...(selectedOption === 'Paid' && {
                    destination_accounts: allDetails.destinations.map((destination: {
                        purchased_date: any; destination_account_id: number | string, destination_account_name: string, purchased_amount: string | number, receipt_url?: string, notes?: string
                    }) => ({
                        destination_account_id: destination.destination_account_id.toString(),
                        destination_account_name: destination.destination_account_name,
                        purchased_date: destination.purchased_date,
                        purchased_amount: Number(destination.purchased_amount),
                        receipt_url: destination.receipt_url,
                        ...(destination.notes && { note: destination.notes }),
                    }))
                }),
                ...(selectedOption === 'Paid' && { credit_status: creditStatusLocal }),
            },
        };
        const totalSourceAmount = allDetails.sources.reduce((total: number, source: any) => {
            const amount = parseFloat(source.purchased_amount) || 0;
            return total + amount;
        }, 0);
        if (totalSourceAmount === 0) {
            dispatch(
                showToast({
                    type: "error",
                    message: `Total ${selectedOption === 'Paid' ? 'Source and Destination' : ''} Amount must be greater than zero.`,
                })
            );
            setLoading(false);
            return;
        }
        if (selectedOption === 'Paid') {

            const totalDestinationAmount = allDetails.destinations.reduce((total: number, destination: any) => {
                const amount = parseFloat(destination.purchased_amount) || 0;
                return total + amount;
            }, 0);


            if (totalSourceAmount !== totalDestinationAmount) {
                dispatch(
                    showToast({
                        type: "error",
                        message: "Total Source and Destination Amount must be equal.",
                    })
                );
                setLoading(false);
                return;
            }
        }
        let response = await PostAPI('manual-entries', payload);
        if (!response?.error) {
            dispatch(
                showToast({
                    type: "success",
                    message: response?.data?.message,
                })
            );
            navigate(`/mno-management/list-of-mnos/view-mno/${id}/purchased-amount-transaction`);
        } else {
            console.log(response, 'API ERROR')
            dispatch(
                showToast({
                    type: "error",
                    message: response?.data?.response?.data?.message,
                })
            );
        }
        setLoading(false);
        return payload;
    };
    const handleModeChange = (value: string) => {
        if (value === "Paid" || value === "Advance") {
            setSelectedOption(value);
            dispatch(setMode(value));
            dispatch(resetManualState())
        }
    };
    const handleCreditStatus = (id: string, checked: boolean) => {
        setCreditStatusLocal(checked);
        dispatch(setCreditStatus(checked));
    };
    return (
        <div>
            <Breadcrumbs
                navPath={["MNO Management", "List of MNO's", "View Details", "List of Purchased Amount Transaction", "Add Manual"]}
                heading="Add Manual"
                noNavigation
                links={["/mno-management/list-of-mnos", "/mno-management/list-of-mnos", `/mno-management/list-of-mnos/view-mno/${id}`, `/mno-management/list-of-mnos/view-mno/${id}/purchased-amount-transaction`]}
                hasAddPrivilege={true}
            />
            <AddModel>
                <div className="flex w-full"  dir={notEnglish ? 'rtl' : 'ltr'}>
                    <div className={`w-1/7 ${selectedOption === 'Paid' ? `${notEnglish ? 'border-l pl-3' : 'border-r pr-3'} border-[#E5E9EB]` : ''} mb-3`}>
                        <h1 className={`font-semibold ${notEnglish ? 'mr-3' : 'ml-3'} mb-1`}>Purchase Type</h1>
                        <CustomRadioButton
                            options={[
                                { id: "Paid", label: "Paid" },
                                { id: "Advance", label: "Advance" },
                            ]}
                            selected={selectedOption}
                            onChange={handleModeChange}
                            dir={notEnglish ? 'rtl' : 'ltr'}
                        />
                    </div>
                    {selectedOption === 'Paid' && <div className={`w-1/7 ${notEnglish ? 'mr-3' : 'ml-3'} h-full px-3`}>
                        <h1 className='font-semibold mb-[15px] ml-1'>Credit Status</h1>
                        <CheckBoxWithLabel
                            label={'Received'}
                            handelCheckBox={(id, checked) => handleCreditStatus(id, checked)}
                            checked={creditStatusLocal}
                            id={`credit_status`}
                            fontWeight='font-[500]'
                        />
                    </div>}
                </div>
                <div className="flex flex-col" dir={notEnglish ? 'rtl' : 'ltr'}>
                    <PurchaseTypeDropdown
                        isSource={true}
                        setErrors={setErrors}
                        errors={errors}
                        singleDestination={true}
                        selectedOption={selectedOption}
                        sourceRefs={sourceRefs}
                    />
                    {selectedOption === 'Paid' && <PurchaseTypeDropdown
                        isSource={false}
                        setErrors={setErrors}
                        errors={errors}
                        selectedOption={selectedOption}
                        sourceRefs={sourceRefs}
                    />}
                </div>
            </AddModel>
            <AddBottomButton
                buttonWidth={'w-[140px]'}
                buttonName={`Add Manual`}
                buttonClass={`w-[140px] whitespace-nowrap`}
                onClick={handleSubmit}
                disabled={false}
                loading={loading}
            />
        </div>
    );
}